import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import {
  MRT_ExpandedState,
  MRT_RowSelectionState,
  MRT_Updater,
} from 'mantine-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BulkTransitionTimesheetsRequest,
  TimesheetDto,
  WorkflowDto,
} from 'src/api/FiretrolApiClient';
import { useTimesheetAdminList } from 'src/api/queries/timesheets/useTimesheetAdminList';
import {
  useBulkTransitionTimesheets,
  useDeleteTimesheet,
  useRejectTimesheet,
} from 'src/api/queries/timesheets/useTimesheetMutations';
import { useAuth } from 'src/hooks/useAuth';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { RejectTimesheetActionIcon } from 'src/routes/Time/TimesheetTab/RejectTimesheetActionIcon';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Modal } from 'src/design/molecules/Modal';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { TimesheetDetailPanel } from 'src/routes/Time/TimesheetTab/TimesheetDetailPanel';
import { splitHoursByOvertime } from 'src/app/timesheets/splitHoursByOvertime';
import { useGroupedTimesheetColumns } from 'src/routes/Time/TimesheetTab/useGroupedTimesheetColumns';
import {
  GroupedTimesheetRow,
  groupRowId,
  groupTimesheetsByUser,
  userIdFromGroupRowId,
} from 'src/routes/Time/TimesheetTab/utils/groupTimesheetsByUser';
import {
  revealRowElementId,
  useRevealExpandedRow,
} from 'src/routes/Time/TimesheetTab/useRevealExpandedRow';
import { useTimeViewParams } from 'src/routes/Time/hooks/useTimeViewParams';
import { getCurrentWorkWeek } from 'src/utils/workWeek';
import { getErrorMessage } from 'src/utils';

interface TimeTimeProps {
  status: string;
  workflow: WorkflowDto;
  stepIndex?: number;
  officeId: number;
  weekId?: number;
  year?: number;
  supervisorId?: number;
}

export const TimesheetTab: React.FC<TimeTimeProps> = ({
  status,
  workflow,
  stepIndex,
  officeId,
  weekId = -1,
  year = -1,
  supervisorId,
}) => {
  const navigate = useNavigate();
  const { isAdmin, scopes } = useAuth();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const {
    data,
    isLoading: loading,
    isError,
  } = useTimesheetAdminList(officeId, {
    status,
    weekId,
    year,
    supervisorId,
  });

  const timesheets = useMemo(() => data ?? [], [data]);

  const bulkTransition = useBulkTransitionTimesheets();
  const rejectMutation = useRejectTimesheet();
  const deleteMutation = useDeleteTimesheet();
  const saving =
    bulkTransition.isPending ||
    rejectMutation.isPending ||
    deleteMutation.isPending;
  const error =
    bulkTransition.error ?? rejectMutation.error ?? deleteMutation.error;

  const isTimesheetAdmin = React.useMemo(
    () => isAdmin || scopes.includes('jobs_timesheets_admin'),
    [isAdmin, scopes],
  );

  const isEmployeeReviewStep = React.useMemo(() => {
    if (stepIndex === undefined) return false;
    return workflow.steps[stepIndex]?.isEmployeeReviewStep === true;
  }, [stepIndex, workflow]);

  const isSupervisorReviewStep = React.useMemo(() => {
    if (stepIndex === undefined) return false;
    return workflow.steps[stepIndex]?.isSupervisorReviewStep === true;
  }, [stepIndex, workflow]);

  const isEditable = React.useMemo(() => {
    if (stepIndex === undefined) return true;
    if (isTimesheetAdmin) return true;
    return workflow.steps[stepIndex]?.editable !== false;
  }, [stepIndex, workflow, isTimesheetAdmin]);

  const isSelectionEnabled = React.useMemo(() => {
    if (stepIndex === undefined) return true; // Draft tab
    if (stepIndex === workflow.steps.length - 1) return false; // Last step
    // Admins can advance past employee/supervisor review steps (e.g. when the
    // employee is on vacation and can't sign via the external email link).
    if ((isEmployeeReviewStep || isSupervisorReviewStep) && !isTimesheetAdmin)
      return false;
    return true;
  }, [
    stepIndex,
    workflow,
    isEmployeeReviewStep,
    isSupervisorReviewStep,
    isTimesheetAdmin,
  ]);

  const isRejectEnabled = React.useMemo(() => {
    if (stepIndex === undefined || stepIndex < 0) return false;

    if (status === 'Draft' || status === 'Rejected') return false;

    const nextStep = workflow.steps[stepIndex + 1];
    return nextStep?.allowReject ?? false;
  }, [stepIndex, workflow, status]);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  // Clear selection when switching tabs (status changes)
  useEffect(() => {
    setRowSelection({});
  }, [status]);

  const groupedData = useMemo(
    () => groupTimesheetsByUser(timesheets),
    [timesheets],
  );

  // Count selected users and derive all timesheet IDs from selected user groups
  const { selectedUserCount, selectedTimesheetIds } = useMemo(() => {
    const userIds = Object.keys(rowSelection);
    const timesheetIds: string[] = [];
    userIds.forEach((userId) => {
      const group = groupedData.find((g) => g.id === userId);
      if (group?.subRows) {
        timesheetIds.push(...group.subRows.map((ts) => ts.id));
      }
    });
    return {
      selectedUserCount: userIds.length,
      selectedTimesheetIds: timesheetIds,
    };
  }, [rowSelection, groupedData]);

  useEffect(() => {
    setRowSelection({});
  }, [timesheets]);

  const transitionTimesheets = async () => {
    // Get all timesheets from selected user groups
    const target = timesheets.filter(
      (ts) => ts.id && selectedTimesheetIds.includes(ts.id),
    );

    const updatedTarget: BulkTransitionTimesheetsRequest = {
      timesheets: target.map((t) => {
        const nextStepIndex = t.stepIndex + 1;
        const nextStatus = t.workflow?.steps[nextStepIndex].label || 'N/A';
        const isComplete = !t.workflow?.steps[nextStepIndex + 1];
        return {
          id: t.id,
          stepIndex: nextStepIndex,
          status: nextStatus,
          isComplete: isComplete,
        };
      }),
    };

    const result = await bulkTransition
      .mutateAsync(updatedTarget)
      .catch(() => null);
    if (!result) return;
    setRowSelection({});
    setConfirmModalOpen(false);
  };

  const rejectTimesheet = (timesheet: TimesheetDto, justification: string) => {
    if (!timesheet || stepIndex === undefined) return;
    rejectMutation.mutate({
      timesheetId: timesheet.id,
      request: { status: 'Rejected', justification },
    });
  };

  const deleteTimesheet = (timesheet: TimesheetDto) => {
    deleteMutation.mutate(timesheet.id);
  };

  const { expandedUserIds, setExpandedUserIds } = useTimeViewParams();
  const expandedGroupRowIds = useMemo(
    () => expandedUserIds.map(groupRowId),
    [expandedUserIds],
  );
  const [expanded, setExpanded] = useState<MRT_ExpandedState>(() =>
    Object.fromEntries(expandedGroupRowIds.map((id) => [id, true])),
  );

  const handleExpandedChange = (updater: MRT_Updater<MRT_ExpandedState>) => {
    const next = typeof updater === 'function' ? updater(expanded) : updater;
    setExpanded(next);
    setExpandedUserIds(
      next === true
        ? groupedData.map((group) => group.userId)
        : Object.keys(next)
            .filter((rowId) => next[rowId])
            .map(userIdFromGroupRowId)
            .filter((userId): userId is number => userId !== null),
    );
  };

  const columns = useGroupedTimesheetColumns();

  const tableState = useTableState({
    identifier: `timesheets-admin-grouped-v3-${officeId}`,
    columns: columns,
    defaultSorting: [{ id: 'employee', desc: false }],
  });

  // Helper to check if a row is a parent (user group) row
  const isParentRow = (
    row: GroupedTimesheetRow | TimesheetDto,
  ): row is GroupedTimesheetRow => {
    return 'subRows' in row && Array.isArray(row.subRows);
  };

  const tableExports = useTableExports<GroupedTimesheetRow>(
    `timesheets-${status.toLowerCase()}`,
    () => {
      // Flatten grouped data for export
      return timesheets.map((item) => {
        const { regularHours, overtimeHours } = splitHoursByOvertime(item.rows);
        return {
          ['Employee']: item.user?.fullName || '',
          ['Week']: item.weekId,
          ['Status']: item.status,
          ['Hours']: regularHours,
          ['Overtime']: overtimeHours,
          ['Job']: item.job?.name || '',
          ['Created By']: item.createdBy?.fullName || '',
        };
      });
    },
  );

  const { table, config } = useTable({
    ...tableState,
    rowSelection: rowSelection,
    expanded,
    tableOptions: {
      enableRowSelection: (row) =>
        isSelectionEnabled && isParentRow(row.original),
      enableSubRowSelection: false,
      enableRowActions: true,
      enableClickToCopy: false,
      enableExpanding: true,
      enableExpandAll: true,
      onExpandedChange: handleExpandedChange,
      mantineTableBodyRowProps: ({ row, isDetailPanel }) =>
        isDetailPanel ? {} : { id: revealRowElementId(row.id) },
      displayColumnDefOptions: { 'mrt-row-actions': { size: 160 } },

      columns: columns,
      data: groupedData,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      filterFromLeafRows: true,
      onRowSelectionChange: setRowSelection,
      getSubRows: (row) => (isParentRow(row) ? row.subRows : undefined),
      renderTopToolbarCustomActions: () => (
        <Group gap="sm">
          {isSelectionEnabled && (
            <BasicButton
              disabled={
                selectedUserCount === 0 || !isSelectionEnabled || saving
              }
              loading={saving}
              onClick={() => setConfirmModalOpen(true)}
            >
              {`Submit ${
                selectedTimesheetIds.length
              } timesheets (${selectedUserCount} user${
                selectedUserCount !== 1 ? 's' : ''
              })`}
            </BasicButton>
          )}
          {isEmployeeReviewStep && !isTimesheetAdmin && (
            <Text size="xs" c="dimmed">
              Timesheets at this step are pending employee review
            </Text>
          )}
          {isSupervisorReviewStep && !isTimesheetAdmin && (
            <Text size="xs" c="dimmed">
              Timesheets at this step are pending supervisor review
            </Text>
          )}
          {(isEmployeeReviewStep || isSupervisorReviewStep) &&
            isTimesheetAdmin && (
              <Text size="xs" c="dimmed">
                Admin bypass: advancing will skip the pending{' '}
                {isEmployeeReviewStep ? 'employee' : 'supervisor'} review email.
              </Text>
            )}
          {!isRejectEnabled && (
            <Text size="xs">
              Note: Timesheets cannot be rejected at this step
            </Text>
          )}
        </Group>
      ),
      renderRowActions: ({ row }) => {
        // Only show actions for child rows (individual timesheets)
        if (isParentRow(row.original)) {
          return null;
        }
        const timesheet = row.original as TimesheetDto;
        return (
          <Group gap="sm" wrap="nowrap">
            <ViewActionIcon
              onClick={() =>
                navigate(
                  `/jobs/${timesheet.jobId}/timesheets/user/${
                    timesheet.userId
                  }/week/${timesheet.year ?? getCurrentWorkWeek().year}/${
                    timesheet.weekId
                  }`,
                )
              }
            />
            <RejectTimesheetActionIcon
              onReject={(justification) =>
                rejectTimesheet(timesheet, justification)
              }
              disabled={saving || !isRejectEnabled}
            />
            {isTimesheetAdmin && (
              <DeleteActionIcon
                requiredConfirmation={true}
                confirmationTitle="Delete timesheet?"
                confirmationText={`Delete ${
                  timesheet.user?.fullName ?? 'this user'
                }'s Week ${timesheet.weekId} timesheet? This cannot be undone.`}
                onDelete={() => deleteTimesheet(timesheet)}
                disabled={saving}
              />
            )}
          </Group>
        );
      },
      renderDetailPanel: ({ row }) => {
        // Only show detail panel for child rows (individual timesheets)
        if (isParentRow(row.original)) {
          return null;
        }
        return (
          <TimesheetDetailPanel timesheet={row.original as TimesheetDto} />
        );
      },
    },
    getRowId: (row) => row.id,
    totalCount: groupedData.length,
    isLoading: loading,
    isFetching: false,
    isError,
    ...tableExports,
  });

  useRevealExpandedRow({
    table: config,
    rowIds: expandedGroupRowIds,
    ready: !loading && tableState.isRestored,
  });

  return (
    <Stack gap="md">
      {error && <Alert color="red">{getErrorMessage(error)}</Alert>}
      {!isEditable && (
        <Alert color="blue" variant="light">
          Data edits are not allowed at this step.
        </Alert>
      )}
      {table}
      <Modal
        title="Confirm Submission"
        opened={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        actions={[
          <BasicButton
            key="submit"
            loading={saving}
            onClick={transitionTimesheets}
          >
            Submit
          </BasicButton>,
        ]}
      >
        <Text>
          Are you sure you want to submit {selectedTimesheetIds.length}{' '}
          timesheet
          {selectedTimesheetIds.length !== 1 ? 's' : ''} for {selectedUserCount}{' '}
          user{selectedUserCount !== 1 ? 's' : ''}?
        </Text>
      </Modal>
    </Stack>
  );
};
